/**
 * マイページのヤギコンシェルジュ → 全画面のAIキャリア相談へ、会話を引き継ぐための受け渡し。
 *
 * AI 側の記憶はサーバーが持っている。`career_ai_service._build_history()` が
 * `career_ai_chat_log` を **session_id で引き直して** 文脈を組み立てるため、
 * **同じ session_id を使い続ければ会話は途切れない**（クライアントは履歴を送っていない）。
 * `chat/start` は新しい id を発行するだけで古い id を無効化せず、`chat/end` も
 * 圧縮ジョブの目印を立てるだけなので、引き継ぎ時は端に置いた id をそのまま使う。
 *
 * 足りないのは画面に出す吹き出しだけなので、表示用のメッセージも一緒に渡す。
 *
 * 遠い画面（マイページ）から別ルート（/ai/career）へセッション状態を渡すので、
 * CLAUDE.md の判定表どおり Pinia に置く（mitt の一過性通知では受け取り側が
 * マウント前で取り逃がす）。
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

/** CareerAI が表示に使うメッセージ。役割は最小限だけ写す */
export type CareerAiHandoverMessage = {
  role: string
  text: string
  jobIds?: number[]
  suggestMode?: string | null
  modeSwitchDismissed?: boolean
}

export type CareerAiHandoverPayload = {
  /** 預けた本人。別ユーザーに前の会話を見せないため、取り出すときに照合する */
  uuid: string
  sessionId: string
  mode: string
  messages: CareerAiHandoverMessage[]
  /**
   * 求人カードの実体。メッセージが持つのは jobIds だけで、カードの中身は
   * CareerAI の jobsCache にしかない。渡さないと引き継いだカードが全部消える
   */
  jobsCache: Record<string, unknown>
  /**
   * 1問目に答える前に全画面へ移ったときの、未送信のボット発言のモード。
   * 渡さないと遷移先で文脈が抜けたまま回答が送られる
   */
  kickoffMode?: string
  /**
   * ユーザーが明示的に選んだ機能のモード。`kickoffMode` はこれが立っている
   * ときだけサーバーに受理されるため、一緒に運ばないと文脈が入らない
   */
  lockedMode?: string
  /**
   * 受け取る画面のパス。受け取り手が描かれない経路（未加入のペイウォール等）で
   * 残った荷物を、別の画面が拾って復元しないようにする
   */
  target?: string
}

export const useCareerAiHandoverStore = defineStore('careerAiHandover', () => {
  const payload = ref<CareerAiHandoverPayload | null>(null)

  const stash = (value: CareerAiHandoverPayload): void => {
    payload.value = value
  }

  /**
   * 引き継ぎを1回だけ取り出す。読んだら消すので、リロードや再訪では
   * 通常どおり新しいセッションから始まる（古い会話が蘇らない）。
   * 遷移に失敗して残ったまま別ユーザーがログインした場合に前の会話を
   * 見せないよう、預けた uuid と一致するときだけ返す
   */
  const take = (
    uuid: string,
    target?: string
  ): CareerAiHandoverPayload | null => {
    const value = payload.value
    payload.value = null
    if (!value || !uuid || value.uuid !== uuid) return null
    // 宛先違いは捨てる（取り出した時点で消えているので、次の画面にも残らない）
    if (target && value.target && value.target !== target) return null
    return value
  }

  return { payload, stash, take }
})
